import {
  escapeHtml,
  parseLocalDate,
  formatDateString,
  statusCssVariable,
} from "../utils";

const MAX_VISIBLE_CHIPS = 3;
const TOTAL_CELLS = 42; // 7 columns × 6 rows; covers any month layout

// MonthView renders a 7×6 calendar grid. Each cell shows a day number and up
// to 3 event chips, with a "+N more" indicator on overflow. Clicking a cell
// emits `calendar:date-cell-click { date }` so the host can drill into Day.
// Clicking a chip emits the standard `calendar:event-click`.
//
// No drag/resize on month — granularity isn't useful and chips are too small.
export class MonthView {
  constructor(scheduler) {
    this.scheduler = scheduler;
    this.element = null;
    this._domListeners = [];
  }

  mount(parent) {
    this.element = document.createElement("div");
    this.element.className =
      "calendar-month-view bg-white rounded-lg border border-gray-200 overflow-hidden";
    parent.appendChild(this.element);
    this._render();
  }

  setData() {
    if (!this.element) return;
    this._render();
  }

  destroy() {
    this._tearDownDomListeners();
    if (this.element?.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }

  // ---- internal ----

  _render() {
    this._tearDownDomListeners();
    this.element.replaceChildren();

    this.element.appendChild(this._buildHeader());
    this.element.appendChild(this._buildGrid());
  }

  _buildHeader() {
    const header = document.createElement("div");
    header.className = "grid border-b border-gray-200";
    header.style.gridTemplateColumns = "repeat(7, minmax(0, 1fr))";
    header.style.backgroundColor = "var(--calendar-header-bg)";

    const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    dayLabels.forEach((label) => {
      const cell = document.createElement("div");
      cell.className =
        "p-2 text-center font-bold text-gray-500 border-r border-gray-200 last:border-r-0";
      cell.style.fontSize = "var(--calendar-time-label-size)";
      cell.textContent = label;
      header.appendChild(cell);
    });

    return header;
  }

  _buildGrid() {
    const grid = document.createElement("div");
    grid.className = "grid";
    grid.style.gridTemplateColumns = "repeat(7, minmax(0, 1fr))";

    const dates = this._gridDates();
    const eventsByDate = this._indexEventsByDate(dates);
    const todayString = formatDateString(new Date());

    dates.forEach((date) => {
      const dateString = formatDateString(date);
      const inCurrentMonth = date.getMonth() === this._anchorMonth();
      const isToday = dateString === todayString;
      const events = eventsByDate.get(dateString) || [];
      grid.appendChild(this._buildCell(date, dateString, inCurrentMonth, isToday, events));
    });

    return grid;
  }

  _buildCell(date, dateString, inCurrentMonth, isToday, events) {
    const compact = this.scheduler.compact;
    const cell = document.createElement("div");
    cell.className =
      "calendar-month-cell border-r border-b border-gray-200 last:border-r-0 cursor-pointer transition-colors p-1";
    cell.style.minHeight = compact
      ? "60px"
      : "var(--calendar-month-cell-min-height)";
    cell.style.borderColor = "var(--calendar-grid-line)";
    cell.dataset.date = dateString;
    if (!inCurrentMonth) {
      cell.style.backgroundColor = "#fafafa";
    }

    // Day number
    const dayBadge = document.createElement("div");
    dayBadge.className =
      "flex items-center justify-end px-1 mb-1";
    const dayNumber = document.createElement("span");
    dayNumber.className = "text-xs font-medium";
    if (isToday) {
      dayNumber.className =
        "inline-flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-xs font-bold";
    } else if (!inCurrentMonth) {
      dayNumber.className = "text-xs text-gray-400";
    } else {
      dayNumber.className = "text-xs text-gray-700 font-medium";
    }
    dayNumber.textContent = String(date.getDate());
    dayBadge.appendChild(dayNumber);
    cell.appendChild(dayBadge);

    if (compact) {
      // Mobile: just a small count badge if there are events. No chips —
      // chips are unreadable at this width. Tap drills into Day view.
      if (events.length > 0) {
        const badge = document.createElement("div");
        badge.className =
          "flex items-center justify-center mt-1";
        const dot = document.createElement("span");
        dot.className =
          "inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-black text-white text-[10px] font-bold";
        dot.textContent = String(events.length);
        badge.appendChild(dot);
        cell.appendChild(badge);
      }
    } else {
      // Desktop: chips with "+N more" overflow.
      const chipsContainer = document.createElement("div");
      chipsContainer.className = "flex flex-col gap-0.5 px-1";
      const visible = events.slice(0, MAX_VISIBLE_CHIPS);
      visible.forEach((item) =>
        chipsContainer.appendChild(this._buildChip(item))
      );
      if (events.length > MAX_VISIBLE_CHIPS) {
        const overflow = document.createElement("button");
        overflow.type = "button";
        overflow.className =
          "text-xs text-gray-500 font-medium text-left hover:text-black";
        overflow.textContent = `+${events.length - MAX_VISIBLE_CHIPS} more`;
        const click = (event) => {
          event.stopPropagation();
          this._emitDateCellClick(event, dateString);
        };
        overflow.addEventListener("click", click);
        this._domListeners.push({
          target: overflow,
          event: "click",
          handler: click,
        });
        chipsContainer.appendChild(overflow);
      }
      cell.appendChild(chipsContainer);
    }

    // Hover + click on the cell itself (excluding chip clicks which stopPropagation)
    const enter = () => {
      cell.style.backgroundColor = inCurrentMonth
        ? "var(--calendar-empty-slot-hover)"
        : "#f5f5f5";
    };
    const leave = () => {
      cell.style.backgroundColor = inCurrentMonth ? "" : "#fafafa";
    };
    const click = (event) => this._emitDateCellClick(event, dateString);

    cell.addEventListener("mouseenter", enter);
    cell.addEventListener("mouseleave", leave);
    cell.addEventListener("click", click);
    this._domListeners.push(
      { target: cell, event: "mouseenter", handler: enter },
      { target: cell, event: "mouseleave", handler: leave },
      { target: cell, event: "click", handler: click }
    );

    return cell;
  }

  _buildChip(item) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className =
      "calendar-event-chip text-left px-1.5 py-0.5 border-l-2 truncate hover:opacity-80";
    chip.style.fontSize = "var(--calendar-event-text-size)";
    chip.style.borderRadius = "2px";
    chip.style.backgroundColor =
      item.kind === "block"
        ? "var(--calendar-block-bg)"
        : "var(--calendar-event-bg)";
    chip.style.borderLeftColor = `var(${statusCssVariable(item)})`;
    chip.dataset.eventId = String(item.id);
    chip.dataset.kind = item.kind;
    chip.dataset.status = item.status || "";

    const time = String(item.start).split("T")[1]?.slice(0, 5) || "";
    const label = this._extractText(item.bodyHtml || "") || item.kind;
    chip.textContent = time ? `${time} ${label}` : label;

    const click = (event) => {
      event.stopPropagation();
      this._emitEventClick(event, item);
    };
    chip.addEventListener("click", click);
    this._domListeners.push({ target: chip, event: "click", handler: click });

    return chip;
  }

  _gridDates() {
    const anchor = parseLocalDate(this.scheduler.date);
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const dayOfWeek = first.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
    const offsetToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const start = new Date(
      first.getFullYear(),
      first.getMonth(),
      first.getDate() - offsetToMonday
    );
    return Array.from({ length: TOTAL_CELLS }, (_, i) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    );
  }

  _anchorMonth() {
    return parseLocalDate(this.scheduler.date).getMonth();
  }

  _indexEventsByDate(dates) {
    const visibleDates = new Set(dates.map((d) => formatDateString(d)));
    const map = new Map();
    this.scheduler.events.forEach((item) => {
      const eventDate = String(item.start).split("T")[0];
      if (!visibleDates.has(eventDate)) return;
      if (!map.has(eventDate)) map.set(eventDate, []);
      map.get(eventDate).push(item);
    });
    // Sort each day's events by start time so chip order matches the timeline.
    map.forEach((list) => list.sort((a, b) => String(a.start).localeCompare(String(b.start))));
    return map;
  }

  // Strip HTML tags from bodyHtml for chip-line display.
  // Cheap: take the first text-node-ish content.
  _extractText(html) {
    if (!html) return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    const text = tmp.textContent || "";
    return text.trim().split("\n").map((s) => s.trim()).filter(Boolean)[0] || "";
  }

  _emitDateCellClick(event, dateString) {
    this.scheduler._emit("date-cell-click", {
      date: dateString,
      clientX: event.clientX,
      clientY: event.clientY,
    });
  }

  _emitEventClick(event, item) {
    this.scheduler._emit("event-click", {
      kind: item.kind,
      id: String(item.id),
      appointmentId: item.kind === "appointment" ? String(item.id) : null,
      blockId: item.kind === "block" ? String(item.id) : null,
      clientX: event.clientX,
      clientY: event.clientY,
    });
  }

  _tearDownDomListeners() {
    this._domListeners.forEach(({ target, event, handler }) =>
      target.removeEventListener(event, handler)
    );
    this._domListeners = [];
  }
}
